import { ListChecks, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";

import { EmptyState } from "../data-display/EmptyState";

interface ChecklistTemplate {
  id: string;
  name: string;
  description: string | null;
  items: string[];
}

const inputClass =
  "rounded-md border border-input bg-transparent px-2 py-1.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Author the checklists that get applied to tasks.
 *
 * Items are edited one per line. That is deliberately not a drag-and-drop
 * list: the people writing "AC install walkthrough" checklists think in
 * lines, paste from notes apps, and reorder by cutting a line, a textarea
 * supports all of that with zero learning curve.
 */
export const ChecklistTemplatesManager = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();

  const { data: templates } = useGetList<ChecklistTemplate>(
    "checklist_templates",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
    },
  );

  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [name, setName] = useState("");
  const [itemsText, setItemsText] = useState("");

  const startNew = () => {
    setEditing("new");
    setName("");
    setItemsText("");
  };

  const startEdit = (template: ChecklistTemplate) => {
    setEditing(template.id);
    setName(template.name);
    setItemsText((template.items ?? []).join("\n"));
  };

  const parsedItems = () =>
    itemsText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

  const save = () => {
    const items = parsedItems();
    if (!name.trim()) {
      notify("Give the template a name", { type: "warning" });
      return;
    }
    if (items.length === 0) {
      notify("Add at least one checklist item", { type: "warning" });
      return;
    }
    const data = { name: name.trim(), items };
    const options = {
      mutationMode: "pessimistic" as const,
      onSuccess: () => {
        notify(editing === "new" ? "Template created" : "Template updated");
        setEditing(null);
        refresh();
      },
      onError: (error: unknown) =>
        notify(error instanceof Error ? error.message : "Could not save", {
          type: "error",
        }),
    };
    if (editing === "new") {
      create("checklist_templates", { data }, options);
    } else {
      const previous = templates?.find((t) => t.id === editing);
      update(
        "checklist_templates",
        { id: editing!, data, previousData: previous },
        options,
      );
    }
  };

  const remove = (template: ChecklistTemplate) =>
    deleteOne(
      "checklist_templates",
      { id: template.id, previousData: template },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          // Applied checklists are copies on the task, so deleting the
          // template never touches work already in flight.
          notify("Template deleted. Checklists already applied are unchanged.");
          refresh();
        },
      },
    );

  const list = templates ?? [];

  return (
    <div className="flex flex-col gap-3">
      {list.length === 0 && editing === null ? (
        <EmptyState
          icon={ListChecks}
          title="No checklist templates yet"
          description="Write a checklist once, “AC install walkthrough”, “Season start-up”, and apply it to any task in one click."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {list.map((template) => (
            <li
              key={template.id}
              className="flex items-start justify-between gap-3 rounded-md border border-border-subtle px-3 py-2"
            >
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => startEdit(template)}
              >
                <p className="text-[13.5px] font-medium text-foreground">
                  {template.name}
                </p>
                <p className="truncate text-[12px] text-muted-foreground">
                  {(template.items ?? []).length} step
                  {(template.items ?? []).length === 1 ? "" : "s"} ·{" "}
                  {(template.items ?? []).slice(0, 3).join(" · ")}
                </p>
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => remove(template)}
                aria-label={`Delete ${template.name}`}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {editing !== null ? (
        <div className="flex flex-col gap-2 rounded-md border border-border p-3">
          <input
            className={inputClass}
            placeholder="Template name (AC install walkthrough)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <textarea
            className={`${inputClass} min-h-28 resize-y`}
            placeholder={
              "One step per line:\nConfirm access\nPhotograph the unit\nTest airflow"
            }
            value={itemsText}
            onChange={(e) => setItemsText(e.target.value)}
          />
          <p className="text-[11.5px] text-muted-foreground">
            One step per line, applied in order.
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={save}>
              {editing === "new" ? "Create template" : "Save changes"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={startNew}
        >
          <Plus className="mr-1 size-3.5" />
          New checklist template
        </Button>
      )}
    </div>
  );
};
